import { useCallback } from "react";
import { useSearchParams } from "react-router";

const getPageFromSearchParams = (queryParams: URLSearchParams): number => {
    const pageFromUrl = queryParams.get("page");
    const pageParam = pageFromUrl ? Number.parseInt(pageFromUrl) : 1;
    return isNaN(pageParam) ? 0 : pageParam - 1;
};

/**
 * Hook for managing page query parameter.
 *
 * If the query parameter is not set, zero is returned.
 * Setting the page to zero will remove the query parameter.
 *
 * The page in query parameter is +1 than in the code.
 *
 * @example
 * const [page, setPage] = usePageParam();
 */
export const usePageParam = (): [number, (page: number) => void] => {
    const [queryParams, setQueryParams] = useSearchParams();
    const page = getPageFromSearchParams(queryParams);
    const setPage = useCallback(
        (newPage: number): void => {
            setQueryParams((prev) => {
                const page = getPageFromSearchParams(prev);
                if (page === newPage) {
                    return prev;
                }

                if (newPage === 0) {
                    prev.delete("page");
                    return prev;
                }

                prev.set("page", (newPage + 1).toString());
                return prev;
            });
        },
        [setQueryParams],
    );

    return [page, setPage];
};
